/*
 * @author BSG <dev@bsgroup.eu>
 * @copyright Better Software Group S.A.
 * @version: 1.0
 */
import type { UrlObject } from "url";
import { ROUTES } from "@nf/constants";
import {
  ActionType,
  MediaStreamType,
  MediaType,
  ScreenType,
  IMediaModel,
  IQueryParams,
  UrlHelper,
  slugify,
  UrlQuery,
  getPrimaryLanguage,
} from "@nf/common";
import { resolveCountry, resolveLanguage } from "helpers";

const MADS_MIKKELSEN_PERSON_ID = "c3e0c62ee91db8dc7382bde7419bb573";
const DRUK_MOVIE_PATH = "druk/18087_929372c";

export class RouteHelper {
  static getIdPathValue(media: IMediaModel) {
    switch (media.MediaTypeCode) {
      case MediaType.Series:
        return `${media.Id}`;
      case MediaType.Episode:
        return `${media.ParentMediaId}`;
      case MediaType.Package:
        return `${media.Id}`;
      case MediaType.Collection:
        return `${media.Id}`;
      default:
        return `${media.Id}`;
    }
  }

  static getDetailsPath(media: IMediaModel) {
    const formattedTitle = slugify(media.Title);
    let path: string;

    switch (media.MediaTypeCode) {
      case MediaType.Series:
        path = `${ROUTES.SERIES_DETAILS_SCREEN}/${formattedTitle}/${media.Id}`;
        break;
      case MediaType.Episode:
        path = `${ROUTES.SERIES_DETAILS_SCREEN}/${formattedTitle}/${media.ParentMediaId}`;
        break;
      case MediaType.Collection:
        path = `${ROUTES.COLLECTION_DETAILS_SCREEN}/${formattedTitle}/${media.Id}`;
        break;
      default:
        path = `${ROUTES.MOVIE_DETAILS_SCREEN}/${formattedTitle}/${media.Id}`;
        break;
    }

    return path;
  }

  static getMainCanonicalUrl(country: string) {
    const primaryLanguage = getPrimaryLanguage(country);
    return `${process.env.NEXT_PUBLIC_MAIN_CANONICAL}/${country}/${primaryLanguage}`;
  }

  /**
   * Construct a canonical URL using the current country and its primary language.
   */
  static getCountryCanonicalUrl(
    pathname: string,
    query: Record<string, string | string[] | undefined>
  ): string {
    const currentCountry = resolveCountry(query.country);
    const primaryLanguage = getPrimaryLanguage(currentCountry);

    const personId = Array.isArray(query.person)
      ? query.person[0]
      : query.person;
    const isSearchPage = pathname.includes(ROUTES.SEARCH_SCREEN);

    if (isSearchPage && personId === MADS_MIKKELSEN_PERSON_ID) {
      return `${process.env.NEXT_PUBLIC_MAIN_CANONICAL}/${currentCountry}/${primaryLanguage}${ROUTES.MOVIE_DETAILS_SCREEN}/${DRUK_MOVIE_PATH}`;
    }

    const path = this.replacePathSegments(
      pathname
        .replace("[country]", currentCountry)
        .replace("[language]", primaryLanguage),
      query
    ).toLowerCase();

    return `${process.env.NEXT_PUBLIC_MAIN_CANONICAL}${path}`;
  }

  static getMediaCanonicalUrl(
    media: IMediaModel,
    pathname: string,
    query: Record<string, string | string[] | undefined>
  ) {
    const id = RouteHelper.getIdPathValue(media);
    const title = slugify(media.Title);

    const queryWithMediaSegments = {
      ...query,
      ...(title ? { title } : {}),
      ...(id ? { id } : {}),
    };

    return this.getCountryCanonicalUrl(pathname, queryWithMediaSegments);
  }

  static extractLangFromRoute(
    countrySegment: string | string[] | undefined,
    languageSegment: string | string[] | undefined
  ): string {
    return resolveLanguage(countrySegment, languageSegment);
  }

  /**
   * Replace dynamic segments in the pathname with corresponding query values.
   * @param {string} path - The pathname with dynamic segments.
   * @param {object} query - The query object containing dynamic segment values.
   * @returns {string} - The updated pathname with replaced values.
   */
  static replacePathSegments = (
    path: string,
    query: Record<string, string | string[] | undefined>
  ): string => {
    return path.replace(/\[([^\]]+)\]/g, (_, segment) => {
      const value = query[segment];
      return Array.isArray(value) ? value.join(",") : value || `[${segment}]`;
    });
  };

  static goToDetails(media?: IMediaModel, query: UrlQuery = {}) {
    if (!media) {
      return {};
    }

    let path = "";
    let queryParams: UrlQuery = {
      ...query,
    };

    if (media?.Action?.ActionType === ActionType.OpenScreen) {
      if (
        media.Action.ScreenTypeCode &&
        media.Action.ScreenTypeCode !== ScreenType.Custom
      ) {
        path = `/${media.Action.ScreenTypeCode.toLowerCase().replace(
          "_",
          "-"
        )}`;

        if (media.Action.ScreenId) {
          path += `/${media.Action.ScreenId}`;
        }
      } else {
        path = `/${media.Action.ScreenId}`;
      }

      if (media.Action.QueryParams) {
        queryParams = {
          ...queryParams,
          ...media.Action.QueryParams,
        };
      }
    } else {
      path = RouteHelper.getDetailsPath(media);
    }

    const location: UrlObject = {
      pathname: path,
      query: UrlHelper.stringify(queryParams),
    };
    return location;
  }

  static goToPlayer(media: IMediaModel, query: IQueryParams = {}) {
    let pathname;
    let queryParams: IQueryParams = {
      ...query,
    };

    if (media.Action?.QueryParams) {
      queryParams = {
        ...queryParams,
        ...media.Action.QueryParams,
      };
    }

    switch (media.MediaTypeCode) {
      case MediaType.Channel:
        pathname = ROUTES.PLAYER_CHANNEL_SCREEN;
        break;
      default:
        pathname = ROUTES.PLAYER_SCREEN;
        break;
    }

    const location: UrlObject = {
      pathname: `${pathname}/${media.Id}`,
      query: UrlHelper.stringify(queryParams),
    };

    return location;
  }

  static goToTrial(mediaId: string | number) {
    const location: UrlObject = {
      pathname: `${ROUTES.PLAYER_SCREEN}/${mediaId}`,
      query: MediaStreamType.Trial,
    };

    return location;
  }

  static goToPerson(name: string, id: string) {
    const path = `${ROUTES.PERSON_DETAILS_SCREEN}/${slugify(name)}/${id}`;
    return path;
  }

  static goToGenre(id: string | number) {
    const path = `${ROUTES.GENRES}/${id}`;
    return path;
  }

  static goToSettings(submenuKey?: string) {
    if (!submenuKey) {
      return `${ROUTES.SETTINGS_SCREEN}`;
    }

    return `${ROUTES.SETTINGS_SCREEN}/${submenuKey}`;
  }

  static goToKids() {
    const path = ROUTES.KIDS;
    return path;
  }

  static goToSearch() {
    const path = ROUTES.SEARCH_SCREEN;
    return path;
  }
}
